import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { invokeFunction } from "@/lib/invoke";
import {
  cancelOverrideBody,
  parseCycleCutResult,
  parsePayNowResult,
  parseScheduledCancelResult,
  type CancelOverride,
  type CancelResult,
  type PayNowResult,
} from "@/lib/cancel-balance";

export interface Subscription {
  id: string;
  organization_id: string;
  plan_id: string | null;
  plan_type: string;
  status: string;
  mp_preapproval_id: string | null;
  mp_payer_email: string | null;
  mp_status: string | null;
  next_billing_date: string | null;
  last_billing_date: string | null;
  grace_period_ends_at: string | null;
  last_payment_at: string | null;
  last_payment_amount: number;
  cancelled_at: string | null;
  activated_at: string | null;
  created_at: string;
  expires_at: string | null;
  retry_count: number;
  last_charge_error: string | null;
  payment_mode?: string | null;
  cancel_scheduled_for?: string | null;
  cancel_requested_at?: string | null;
}

export function useSubscription(orgId: string | null) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }

    const { data, error } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("organization_id", orgId)
      .in("status", ["active", "pending", "suspended"])
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!error && data) {
      setSubscription(data as unknown as Subscription);
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  const cancelSubscription = useCallback(async (override?: CancelOverride): Promise<CancelResult> => {
    if (!subscription || !orgId) return { ok: false, error: null, code: null };

    try {
      const { data: preapproval } = await supabase
        .from("mp_preapprovals" as never)
        .select("mp_preapproval_id" as never)
        .eq("organization_id" as never, orgId as never)
        .is("cancelled_at" as never, null as never)
        .in("status" as never, ["pending", "authorized", "paused"] as never)
        .order("created_at" as never, { ascending: false } as never)
        .limit(1)
        .maybeSingle();

      const mpPreapprovalId = (preapproval as { mp_preapproval_id?: string } | null)?.mp_preapproval_id;
      const { data, error, code } = await invokeFunction("mercadopago-preapproval-cancel", {
        ...(mpPreapprovalId ? { mp_preapproval_id: mpPreapprovalId } : { organization_id: orgId }),
        ...cancelOverrideBody(override),
      });
      if (error) {
        console.error("Cancel rejected:", error);
        return { ok: false, error, code: code ?? null };
      }

      await fetchSubscription();
      return { ok: true, error: null, code: null, scheduled: parseScheduledCancelResult(data), cycleCut: parseCycleCutResult(data) };
    } catch (err) {
      console.error("Cancel error:", err);
      return { ok: false, error: null, code: null };
    }
  }, [subscription, orgId, fetchSubscription]);

  const resumeSubscription = useCallback(async (): Promise<CancelResult> => {
    if (!orgId) return { ok: false, error: null, code: null };
    try {
      const { error, code } = await invokeFunction("mercadopago-preapproval-cancel", {
        organization_id: orgId,
        action: "resume",
      });
      if (error) return { ok: false, error, code: code ?? null };
      await fetchSubscription();
      return { ok: true, error: null, code: null };
    } catch (err) {
      console.error("Resume error:", err);
      return { ok: false, error: null, code: null };
    }
  }, [orgId, fetchSubscription]);

  const payAndCancelNow = useCallback(async (): Promise<PayNowResult> => {
    if (!orgId) return { ok: false, error: null, code: null };
    try {
      const { data, error, code } = await invokeFunction("mercadopago-preapproval-cancel", {
        organization_id: orgId,
        action: "pay_now",
      });
      if (error) return { ok: false, error, code: code ?? null };
      const result = parsePayNowResult(data);
      if (result.cancelled) await fetchSubscription();
      return result;
    } catch (err) {
      console.error("Pay now error:", err);
      return { ok: false, error: null, code: null };
    }
  }, [orgId, fetchSubscription]);

  return { subscription, loading, refetch: fetchSubscription, cancelSubscription, resumeSubscription, payAndCancelNow };
}
